



import Vue from 'vue'
import TileLayer from 'ol/layer/Tile'
import TileWMS from 'ol/source/TileWMS'
import Map from 'ol/Map'
import View from 'ol/View'
import XYZ from 'ol/source/XYZ'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import Icon from 'ol/style/Icon'
import Style from 'ol/style/Style'
import { fromLonLat } from 'ol/proj'
import { OPEN_LAYER_GEOJSON_FORMAT } from '@/costanti'
import { Component, Watch } from 'vue-property-decorator'
import { ModalStore } from '@/store/modal'
import { PuntiluceMappaStore } from '@/store/puntiluce/puntiluce-mappa'
// Costanti per la configurazione
const coordinateIniziali = [11.853409605324337, 45.519614857468746]
const zoomIniziale = 8

interface Coordinate {
    latitudine: string
    longitudine: string
}

// tslint:disable-next-line: no-empty-interface
interface RilevamentoBonificato extends Coordinate {
}

interface Rilevamento extends Coordinate {
    stato: string
}

interface HereMapsParams {
    base: string
    scheme: string
    visible: boolean
}

interface GoogleMapsParams {
    layer: string
    visible: boolean
}

interface Data {
    map: Map
    vectorLayerRilevamenti: TileLayer
    vectorLayerConfiniComunali: TileLayer
    vectorLayerHereNormale: TileLayer
    vectorLayerHereSatellite: TileLayer
    styleCache: object
}

@Component
export default class Mappa extends Vue {

    private static createHereMapsTileLayer(params: GoogleMapsParams): TileLayer {
        // const hereAppId = 'Ex3KVTSjrvNbZl4MLVWG'
        // const hereAppCode = 'bgpY7uy-MofYwpC32I8LYw'
        // const urlTpl = `https://{1-4}.{base}.maps.cit.api.here.com/maptile/2.1/maptile/newest/{scheme}/{z}/{x}/{y}/256/png?app_id=${hereAppId}&app_code=${hereAppCode}`
        const urlGMaps = 'http://mt1.google.com/vt/lyrs={layer}&&x={x}&y={y}&z={z}'
        return new TileLayer({
            visible: params.visible,
            preload: Infinity,
            source: new XYZ({
                url: urlGMaps.replace('{layer}', params.layer),
                /*url: urlTpl
                    .replace('{base}', hereMapsParams.base)
                    .replace('{scheme}', hereMapsParams.scheme),*/
                attributions: 'Map Tiles &copy; ' + new Date().getFullYear() + ' ' +
                    '<a href="http://developer.here.com">HERE</a>'
            })
        })
    }

    private static getMapFeature({ latitudine, longitudine, stato, tipoDato }: { latitudine: string, longitudine: string, stato: string, tipoDato: string }) {
        return {
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [parseFloat(longitudine), parseFloat(latitudine)]
            },
            properties: {
                stato: stato,
                tipoDato: tipoDato
            }
        }
    }

    @ModalStore.State('nuoveCoordinate') private nuoveCoordinate: [number, number]
    @PuntiluceMappaStore.State('mostraConfiniComunali') private mostraConfiniComunali: boolean
    @PuntiluceMappaStore.State('mostraMappaSatellitare') private mostraMappaSatellitare: boolean
    private map: Map = null
    private vectorLayerRilevamenti: TileLayer = null
    private vectorLayerConfiniComunali: TileLayer = null
    private vectorLayerHereNormale: TileLayer = null
    private vectorLayerHereSatellite: TileLayer = null
    private styleCache: object = {}

    // quando cambia l'indirizzo autocompletato, abbiamo nuove coordinate in cui andare
    @Watch('nuoveCoordinate')
    private onNuoveCoordinateChange(newValue: [number, number]) {
        // se abbiamo le coordinate
        // ci spostiamo con un'animazione
        this.map.getView().animate({
            center: fromLonLat(newValue),
            zoom: 14,
            duration: 1000
        })
    }

    @Watch('mostraConfiniComunali')
    private onMostraConfiniComunaliChange(newValue: boolean) {
        if (newValue) {
            this.map.addLayer(this.vectorLayerConfiniComunali)
        } else {
            this.map.removeLayer(this.vectorLayerConfiniComunali)
        }
    }

    @Watch('mostraMappaSatellitare')
    private onMostraMappaSatellitareChange(newValue: boolean) {
        if (newValue) {
            this.vectorLayerHereNormale.setVisible(false)
            this.vectorLayerHereSatellite.setVisible(true)
        } else {
            this.vectorLayerHereNormale.setVisible(true)
            this.vectorLayerHereSatellite.setVisible(false)
        }
    }

    private async caricaDati() {
        const [responseQuadri, responsePuntiLuce, responseQuadriBonificati, responsePuntiLuceBonificati] = await Promise.all([
            Vue.axios.get('/api/puntiluce/quadri.json?exclude[]=id&exclude[]=qdr&exclude[]=quad_cab&exclude[]=cod_ut&exclude[]=pod' +
                '&exclude[]=kw&exclude[]=n_fasi&exclude[]=cond_quad&exclude[]=categ&exclude[]=sottoqdr&exclude[]=id_padre&exclude[]=note'),
            Vue.axios.get('/api/puntiluce/puntiluce.json?exclude[]=id&exclude[]=arm&exclude[]=t_prot&exclude[]=ambito_sos' +
                '&exclude[]=hft&exclude[]=t_sost&exclude[]=ambito_arm&exclude[]=lin&exclude[]=sos&exclude[]=qdr&exclude[]=quad_cab' +
                '&exclude[]=sost&exclude[]=pl&exclude[]=num_lmp&exclude[]=mat_sost&exclude[]=cond_sost&exclude[]=p_pozz&exclude[]=terra_sost' +
                '&exclude[]=num_palo&exclude[]=note_sost&exclude[]=stato_arm&exclude[]=t_arm&exclude[]=marca&exclude[]=mod&exclude[]=descr_arm' +
                '&exclude[]=conform_lr&exclude[]=cond_arm&exclude[]=pos&exclude[]=h_pl&exclude[]=l_sbr&exclude[]=note_arm&exclude[]=t_lmp' +
                '&exclude[]=w&exclude[]=note_lmp&exclude[]=w_real&exclude[]=t_lmp_real'),
            Vue.axios.get('/api/puntiluce/quadri_bonificati.json'),
            Vue.axios.get('/api/puntiluce/puntiluce_bonificati.json')])
        const quadri: Rilevamento[] = responseQuadri.data.quadros
        const puntiLuce: Rilevamento[] = responsePuntiLuce.data.punto_luces
        const quadriBonificati: RilevamentoBonificato[] = responseQuadriBonificati.data.quadro_bonificatos
        const puntiLuceBonificati: RilevamentoBonificato[] = responsePuntiLuceBonificati.data.punto_luce_bonificatos
        const features = {
            type: 'FeatureCollection',
            features: [...quadri.map((quadro) => Mappa.getMapFeature({
                latitudine: quadro.latitudine,
                longitudine: quadro.longitudine,
                stato: quadro.stato,
                tipoDato: 'quadro'
            })), ...puntiLuce.map((puntoluce) => Mappa.getMapFeature({
                latitudine: puntoluce.latitudine,
                longitudine: puntoluce.longitudine,
                stato: puntoluce.stato,
                tipoDato: 'puntoluce'
            })), ...quadriBonificati.map((quadro) => Mappa.getMapFeature({
                latitudine: quadro.latitudine,
                longitudine: quadro.longitudine,
                stato: 'Non definito',
                tipoDato: 'quadro_bonificato'
            })), ...puntiLuceBonificati.map((puntoluce) => Mappa.getMapFeature({
                latitudine: puntoluce.latitudine,
                longitudine: puntoluce.longitudine,
                stato: 'Non definito',
                tipoDato: 'puntoluce_bonificato'
            }))]
        }
        // togliamo i rilevamenti precedenti
        this.vectorLayerRilevamenti.getSource().clear()
        // leggiamo i rilevamenti
        this.vectorLayerRilevamenti.getSource().addFeatures(OPEN_LAYER_GEOJSON_FORMAT.readFeatures(features))
    }

    private mounted() {
        this.vectorLayerHereNormale = Mappa.createHereMapsTileLayer({
            layer: 'm',
            // base: 'base',
            // scheme: 'normal.day',
            visible: true
        })
        this.vectorLayerHereSatellite = Mappa.createHereMapsTileLayer({
            layer: 's&hl=pl',
            // base: 'aerial',
            // scheme: 'satellite.day',
            visible: false
        })
        // caichiamo i vettori layer
        this.vectorLayerRilevamenti = new VectorLayer({
            source: new VectorSource({
                features: undefined
            }),
            style: (feature: any, resolution) => {
                const tipoDato = feature.get('tipoDato')
                const statoSegnalazione = feature.get('stato')
                // check the cache and create a new style
                // level if its not been created before.
                if (!this.styleCache[tipoDato + statoSegnalazione]) {
                    let src = '/report/logo.png'
                    if (tipoDato === 'quadro') {
                        if (statoSegnalazione === 'Non definito')
                            src = '/puntiluce/quadro_assegnato.jpg'
                        else
                            src = '/puntiluce/quadro_in_corso.jpg'
                    } else if (tipoDato === 'puntoluce') {
                        if (statoSegnalazione === 'Non definito')
                            src = '/puntiluce/punto_luce_assegnato.png'
                        else
                            src = '/puntiluce/punto_luce_in_corso.png'
                    } else if (tipoDato === 'quadro_bonificato') {
                        src = '/puntiluce/quadro_bonificato.jpg'
                    } else if (tipoDato === 'puntoluce_bonificato') {
                        src = '/puntiluce/punto_luce_bonificato.png'
                    }

                    this.styleCache[tipoDato + statoSegnalazione] = new Style({
                        image: new Icon(({
                            scale: .5,
                            src: src
                        }))
                    })
                }
                // at this point, the style for the current level is in the cache
                // so return it (as an array!)
                return [this.styleCache[tipoDato + statoSegnalazione]]
            }
        })
        // carichiamo la mappa
        this.map = new Map({
            // Improve user experience by loading tiles while dragging/zooming. Will make
            // zooming choppy on mobile or slow devices.
            loadTilesWhileInteracting: true,
            target: 'map',
            controls: [],
            layers: [this.vectorLayerHereNormale, this.vectorLayerHereSatellite],
            view: new View({
                center: fromLonLat([coordinateIniziali[0], coordinateIniziali[1]]),
                zoom: zoomIniziale
            })
        })

        this.map.addLayer(this.vectorLayerRilevamenti)

        const tecnographParams = (layer) => ({
            LAYERS: layer,
            FORMAT: 'image/png',
            SERVICE: 'WMS',
            VERSION: '1.1.1',
            REQUEST: 'GetMap',
            WIDTH: '512',
            HEIGHT: '512',
            TRANSPARENT: 'true',
            SRS: 'EPSG:900913'
        })
        this.vectorLayerConfiniComunali = new TileLayer({
            source: new TileWMS({
                url: 'http://www.tecnograph-group.com/acantho/wms.asp?s=33352308854463727',
                params: tecnographParams('ip_comuni')
            })
        })

        // BUG fix
        // senza questo scazza e schiaccia la mappa all inizio
        setTimeout(() => {
            this.map.updateSize()
        }, 200)
        this.caricaDati()
    }

}
